import React, { useMemo, useRef, useState } from 'react';
import JSZip from 'jszip';
import { Download, FileImage, FileText, FolderOpen, Images, Loader2, Plus, Save, Sparkles, Trash2, UploadCloud } from 'lucide-react';
import { analyzeCardNews, createCardNewsProject, deleteCardNewsProject, fetchCardNewsProjects, getCardNewsProject, updateCardNewsProject } from '../api';
import { canvasToBlob, FONTS, getCardTextStyles, renderCardToCanvas, THEMES } from '../cardNewsRenderer';
import './CardNewsPage.css';

const TEMPLATE_STORAGE_KEY = 'newsmonitor-cardnews-templates-v1';

function loadTemplates() {
  try {
    const templates = JSON.parse(localStorage.getItem(TEMPLATE_STORAGE_KEY) || '[]');
    return Array.isArray(templates) ? templates : [];
  } catch {
    return [];
  }
}

const TYPE_LABELS = {
  cover: '표지',
  number: '숫자 강조',
  quote: '인용문',
  standard: '일반',
  closing: '마무리',
};

function getLayoutDefaults(card, hasPhoto) {
  const isCover = card.type === 'cover';
  const titleY = isCover ? 760 : hasPhoto && card.type !== 'number' ? 800 : card.type === 'number' && card.highlight ? 590 : 390;
  return {
    photo: { x: 82, y: 348, width: 916, height: 390 },
    text: { x: 82, y: titleY, width: 916 },
  };
}

function clampLayoutRegion(region, next) {
  const minimumWidth = region === 'photo' ? 240 : 240;
  const minimumHeight = region === 'photo' ? 140 : 0;
  const width = Math.max(minimumWidth, Math.min(1080, Number(next.width) || minimumWidth));
  const height = Math.max(minimumHeight, Math.min(1350, Number(next.height) || minimumHeight));
  return {
    ...next,
    width,
    height,
    x: Math.max(0, Math.min(1080 - width, Number(next.x) || 0)),
    y: Math.max(0, Math.min(1350 - height, Number(next.y) || 0)),
  };
}

function PreviewCard({ card, index, total, image, themeName, fontName, layoutEditing, onStartMove, onStartResize }) {
  const theme = THEMES[themeName];
  const font = FONTS[fontName] || FONTS.pretendard;
  const isCover = card.type === 'cover';
  const hasPhoto = Boolean(image && card.type !== 'number');
  const cropStyle = {
    objectPosition: `${card.imagePositionX ?? 50}% ${card.imagePositionY ?? 50}%`,
    transform: `scale(${card.imageZoom ?? 1})`,
    transformOrigin: `${card.imagePositionX ?? 50}% ${card.imagePositionY ?? 50}%`,
  };
  const textStyles = getCardTextStyles(card, themeName, fontName);
  const layoutDefaults = getLayoutDefaults(card, hasPhoto);
  const textLayout = clampLayoutRegion('text', { ...layoutDefaults.text, ...card.layout?.text, height: 0 });
  const photoLayout = clampLayoutRegion('photo', { ...layoutDefaults.photo, ...card.layout?.photo });
  const previewTextStyle = (style) => ({
    fontFamily: `"${style.family}", "Malgun Gothic", sans-serif`,
    fontSize: `${(style.size / 1080) * 100}cqw`,
    color: style.color,
    textAlign: style.align,
    lineHeight: style.lineHeight,
  });
  return (
    <div className={`cardnews-preview cardnews-preview-${card.type} ${isCover && hasPhoto ? 'has-image' : ''} ${hasPhoto ? 'has-photo' : ''}`} style={{ fontFamily: `"${font.family}", "Malgun Gothic", sans-serif`, '--cn-primary': theme.primary, '--cn-secondary': theme.secondary, '--cn-accent': theme.accent, '--cn-bg': theme.background, '--cn-text': theme.text, '--cn-muted': theme.muted }}>
      {isCover && hasPhoto && <><img className="cardnews-preview-cover-image" src={image.dataUrl} alt="" style={cropStyle} /><div className="cardnews-preview-cover-overlay" /></>}
      {!isCover && hasPhoto && <div className="cardnews-preview-image-slot" style={{ left: `${(photoLayout.x / 1080) * 100}%`, top: `${(photoLayout.y / 1350) * 100}%`, width: `${(photoLayout.width / 1080) * 100}%`, height: `${(photoLayout.height / 1350) * 100}%` }}><img src={image.dataUrl} alt="" style={cropStyle} /></div>}
      <div className="cardnews-preview-top">{card.eyebrow || 'KNFC NEWS'}</div>
      <div className="cardnews-preview-content" style={{ left: `${(textLayout.x / 1080) * 100}%`, top: `${(textLayout.y / 1350) * 100}%`, width: `${(textLayout.width / 1080) * 100}%`, right: 'auto', bottom: 'auto' }}>
        {card.type === 'quote' && <div className="cardnews-quote-mark">“</div>}
        {card.highlight && <div className="cardnews-highlight" style={previewTextStyle(textStyles.highlight)}>{card.highlight}</div>}
        <div className="cardnews-preview-title" style={previewTextStyle(textStyles.title)}>{card.title}</div>
        <div className="cardnews-preview-body" style={previewTextStyle(textStyles.body)}>{card.body}</div>
      </div>
      <div className="cardnews-preview-footer"><span>한전원자력연료 | www.knfc.co.kr</span><span>{String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</span></div>
      {layoutEditing && <>
        <button type="button" className="cardnews-layout-target text" style={{ left: `${(textLayout.x / 1080) * 100}%`, top: `${(textLayout.y / 1350) * 100}%`, width: `${(textLayout.width / 1080) * 100}%` }} onPointerDown={(event) => onStartMove(event, 'text')}>텍스트 이동<span className="cardnews-layout-resize" onPointerDown={(event) => onStartResize(event, 'text')} /></button>
        {!isCover && hasPhoto && <button type="button" className="cardnews-layout-target photo" style={{ left: `${(photoLayout.x / 1080) * 100}%`, top: `${(photoLayout.y / 1350) * 100}%`, width: `${(photoLayout.width / 1080) * 100}%`, height: `${(photoLayout.height / 1350) * 100}%` }} onPointerDown={(event) => onStartMove(event, 'photo')}>사진 이동<span className="cardnews-layout-resize" onPointerDown={(event) => onStartResize(event, 'photo')} /></button>}
      </>}
    </div>
  );
}

function TypographyControls({ label, prefix, style, minSize, maxSize, updateCard }) {
  const color = /^#[0-9a-f]{6}$/i.test(style.color) ? style.color : '#ffffff';
  return <section className="cardnews-typography-controls">
    <div className="cardnews-typography-heading"><strong>{label} 스타일</strong></div>
    <label>폰트<select className="select" value={style.font} onChange={(event) => updateCard(`${prefix}Font`, event.target.value)}>{Object.entries(FONTS).map(([value, item]) => <option key={value} value={value}>{item.label}</option>)}</select></label>
    <div className="cardnews-control-row">
      <label>크기 <span>{style.size}px</span><input type="range" min={minSize} max={maxSize} step="1" value={style.size} onChange={(event) => updateCard(`${prefix}Size`, Number(event.target.value))} /></label>
      <label>색상<input type="color" value={color} onChange={(event) => updateCard(`${prefix}Color`, event.target.value)} /></label>
    </div>
    <div className="cardnews-control-row">
      <label>정렬<select className="select" value={style.align} onChange={(event) => updateCard(`${prefix}Align`, event.target.value)}><option value="left">왼쪽</option><option value="center">가운데</option><option value="right">오른쪽</option></select></label>
      <label>행간 <span>{style.lineHeight.toFixed(2)}</span><input type="range" min="1" max="2.2" step="0.05" value={style.lineHeight} onChange={(event) => updateCard(`${prefix}LineHeight`, Number(event.target.value))} /></label>
    </div>
  </section>;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`${file.name} 파일을 읽지 못했습니다.`));
    reader.readAsDataURL(file);
  });
}

export default function CardNewsPage() {
  const documentInput = useRef(null);
  const photoInput = useRef(null);
  const [documentFile, setDocumentFile] = useState(null);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [result, setResult] = useState(null);
  const [cards, setCards] = useState([]);
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState([]);
  const [theme, setTheme] = useState('classic');
  const [font, setFont] = useState('pretendard');
  const [activeCard, setActiveCard] = useState(0);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [layoutEditing, setLayoutEditing] = useState(false);
  const [templates, setTemplates] = useState(loadTemplates);
  const [templateName, setTemplateName] = useState('');
  const [projects, setProjects] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [savingProject, setSavingProject] = useState(false);
  const [error, setError] = useState('');

  const images = result?.images || [];
  const selectedCard = cards[activeCard];
  const selectedImage = useMemo(() => images.find((image) => image.id === selectedCard?.imageId), [images, selectedCard]);
  const selectedTextStyles = useMemo(() => selectedCard ? getCardTextStyles(selectedCard, theme, font) : null, [selectedCard, theme, font]);

  React.useEffect(() => {
    fetchCardNewsProjects().then((data) => setProjects(data.projects || [])).catch(() => setProjects([]));
  }, []);
  const selectedLayoutDefaults = selectedCard ? getLayoutDefaults(selectedCard, Boolean(selectedImage)) : null;
  const selectedTextLayout = selectedCard ? clampLayoutRegion('text', { ...selectedLayoutDefaults.text, ...selectedCard.layout?.text, height: 0 }) : null;
  const selectedPhotoLayout = selectedCard ? clampLayoutRegion('photo', { ...selectedLayoutDefaults.photo, ...selectedCard.layout?.photo }) : null;

  async function handlePhotoSelection(event) {
    const selected = Array.from(event.target.files || []).filter((file) => /^image\/(jpeg|png|webp)$/.test(file.type)).slice(0, 6);
    event.target.value = '';
    if (!selected.length) return setError('JPG, PNG 또는 WEBP 사진을 선택해 주세요.');
    setError('');
    if (!result) {
      setPhotoFiles(selected);
      return;
    }
    try {
      const added = await Promise.all(selected.map(async (file, index) => ({
        id: `local-${Date.now()}-${index}`,
        name: file.name,
        mimeType: file.type,
        dataUrl: await readFileAsDataUrl(file),
        source: 'local',
      })));
      setResult((current) => ({ ...current, images: [...(current.images || []), ...added] }));
      setCards((current) => current.map((card, index) => index === activeCard ? { ...card, imageId: added[0].id } : card));
    } catch (photoError) {
      setError(photoError.message);
    }
  }

  async function saveProject() {
    if (!result) return setError('초안을 먼저 만들어 주세요.');
    const title = projectName.trim() || result.document?.name?.replace(/\.[^.]+$/, '') || `카드뉴스 ${new Date().toLocaleString('ko-KR')}`;
    setSavingProject(true);
    setError('');
    try {
      const payload = {
        title,
        documentName: result.document?.name || '',
        cards,
        images: result.images || [],
        caption,
        hashtags,
        theme,
        font,
      };
      if (currentProjectId) {
        await updateCardNewsProject(currentProjectId, payload);
      } else {
        const created = await createCardNewsProject(payload);
        setCurrentProjectId(created.id);
      }
      setProjectName('');
      const list = await fetchCardNewsProjects();
      setProjects(list.projects || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || '프로젝트 저장에 실패했습니다.');
    } finally {
      setSavingProject(false);
    }
  }

  async function loadProject(id) {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const data = await getCardNewsProject(id);
      setCurrentProjectId(data.id);
      setProjectName(data.title || '');
      setCards(data.cards || []);
      setCaption(data.caption || '');
      setHashtags(data.hashtags || []);
      setTheme(data.theme || 'classic');
      setFont(data.font || 'pretendard');
      setResult({
        document: { name: data.documentName || '', paragraphs: [], text: '' },
        images: data.images || [],
        draft: { cards: data.cards || [], caption: data.caption || '', hashtags: data.hashtags || [] },
        generationMode: 'loaded',
      });
      setActiveCard(0);
    } catch (err) {
      setError(err.response?.data?.error || err.message || '프로젝트 불러오기에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }

  async function removeProject(id) {
    if (!window.confirm('선택한 프로젝트를 삭제하시겠습니까?')) return;
    try {
      await deleteCardNewsProject(id);
      if (currentProjectId === id) {
        setCurrentProjectId(null);
        setProjectName('');
      }
      const list = await fetchCardNewsProjects();
      setProjects(list.projects || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || '프로젝트 삭제에 실패했습니다.');
    }
  }

  async function analyze(event) {
    event.preventDefault();
    if (!documentFile) return setError('HWPX 파일을 선택해 주세요.');
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('document', documentFile);
      photoFiles.forEach((file) => formData.append('photos', file));
      const data = await analyzeCardNews(formData);
      const coverImage = data.images?.find((image) => image.source === 'uploaded') || data.images?.[0];
      const nextCards = data.draft.cards.map((card, index) => ({ ...card, imageId: index === 0 ? coverImage?.id || '' : '', imageZoom: 1, imagePositionX: 50, imagePositionY: 50 }));
      setResult(data);
      setCards(nextCards);
      setCaption(data.draft.caption || '');
      setHashtags(data.draft.hashtags || []);
      setActiveCard(0);
      setCurrentProjectId(null);
      setProjectName('');
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || '분석에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }

  function updateCard(field, value) {
    setCards((current) => current.map((card, index) => index === activeCard ? { ...card, [field]: value } : card));
  }

  function updateLayout(region, field, value) {
    setCards((current) => current.map((card, index) => {
      if (index !== activeCard) return card;
      const defaults = getLayoutDefaults(card, Boolean(images.find((image) => image.id === card.imageId)));
      const next = clampLayoutRegion(region, { ...defaults[region], ...card.layout?.[region], [field]: Number(value) });
      return { ...card, layout: { ...card.layout, [region]: next } };
    }));
  }

  function resetLayout() {
    setCards((current) => current.map((card, index) => index === activeCard ? { ...card, layout: undefined } : card));
  }

  function startLayoutMove(event, region) {
    event.preventDefault();
    const preview = event.currentTarget.closest('.cardnews-preview');
    const bounds = preview.getBoundingClientRect();
    const card = cards[activeCard];
    const defaults = getLayoutDefaults(card, Boolean(images.find((image) => image.id === card.imageId)));
    const initial = clampLayoutRegion(region, { ...defaults[region], ...card.layout?.[region] });
    const startX = event.clientX;
    const startY = event.clientY;
    const move = (moveEvent) => {
      const next = clampLayoutRegion(region, { ...initial, x: initial.x + ((moveEvent.clientX - startX) / bounds.width) * 1080, y: initial.y + ((moveEvent.clientY - startY) / bounds.height) * 1350 });
      setCards((current) => current.map((item, index) => index === activeCard ? { ...item, layout: { ...item.layout, [region]: next } } : item));
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  }

  function startLayoutResize(event, region) {
    event.preventDefault();
    event.stopPropagation();
    const preview = event.currentTarget.closest('.cardnews-preview');
    const bounds = preview.getBoundingClientRect();
    const card = cards[activeCard];
    const defaults = getLayoutDefaults(card, Boolean(images.find((image) => image.id === card.imageId)));
    const initial = clampLayoutRegion(region, { ...defaults[region], ...card.layout?.[region] });
    const startX = event.clientX;
    const startY = event.clientY;
    const move = (moveEvent) => {
      const next = clampLayoutRegion(region, {
        ...initial,
        width: initial.width + ((moveEvent.clientX - startX) / bounds.width) * 1080,
        height: region === 'photo' ? initial.height + ((moveEvent.clientY - startY) / bounds.height) * 1350 : initial.height,
      });
      setCards((current) => current.map((item, index) => index === activeCard ? { ...item, layout: { ...item.layout, [region]: next } } : item));
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  }

  function saveTemplates(next) {
    localStorage.setItem(TEMPLATE_STORAGE_KEY, JSON.stringify(next));
    setTemplates(next);
  }

  function saveTemplate() {
    const name = templateName.trim();
    if (!name) return setError('템플릿 이름을 입력해 주세요.');
    const styleFields = ['imageZoom', 'imagePositionX', 'imagePositionY', 'titleFont', 'titleSize', 'titleColor', 'titleAlign', 'titleLineHeight', 'bodyFont', 'bodySize', 'bodyColor', 'bodyAlign', 'bodyLineHeight', 'highlightFont', 'highlightSize', 'highlightColor', 'highlightAlign', 'highlightLineHeight'];
    const settings = Object.fromEntries(styleFields.filter((field) => selectedCard[field] !== undefined).map((field) => [field, selectedCard[field]]));
    saveTemplates([{ id: `template-${Date.now()}`, name, type: selectedCard.type, layout: selectedCard.layout, settings, theme, font }, ...templates]);
    setTemplateName('');
    setError('');
  }

  function applyTemplate(template) {
    setCards((current) => current.map((card, index) => index === activeCard ? { ...card, layout: template.layout, ...template.settings } : card));
    setTheme(template.theme || theme);
    setFont(template.font || font);
  }

  function duplicateTemplate(template) {
    saveTemplates([{ ...template, id: `template-${Date.now()}`, name: `${template.name} 복제` }, ...templates]);
  }

  function deleteTemplate(id) {
    saveTemplates(templates.filter((template) => template.id !== id));
  }

  function addCard() {
    if (cards.length >= 8) return;
    const next = { id: `card-${Date.now()}`, type: 'standard', eyebrow: '핵심 내용', title: '새 카드 제목', body: '카드에 들어갈 내용을 입력하세요.', highlight: '', imageId: '', imageZoom: 1, imagePositionX: 50, imagePositionY: 50 };
    setCards((current) => [...current, next]);
    setActiveCard(cards.length);
  }

  function removeCard() {
    if (cards.length <= 3) return setError('카드는 최소 3장이 필요합니다.');
    setCards((current) => current.filter((_, index) => index !== activeCard));
    setActiveCard((current) => Math.max(0, current - 1));
  }

  async function renderCard(index) {
    const card = cards[index];
    const image = images.find((item) => item.id === card.imageId);
    return renderCardToCanvas(card, index, cards.length, image?.dataUrl, theme, font);
  }

  async function downloadCurrent() {
    setDownloading(true);
    setError('');
    try {
      const canvas = await renderCard(activeCard);
      const blob = await canvasToBlob(canvas);
      downloadBlob(blob, `KNFC_cardnews_${String(activeCard + 1).padStart(2, '0')}.jpg`);
    } catch (downloadError) {
      setError(downloadError.message);
    } finally {
      setDownloading(false);
    }
  }

  async function downloadZip() {
    setDownloading(true);
    setError('');
    try {
      const zip = new JSZip();
      for (let index = 0; index < cards.length; index++) {
        const canvas = await renderCard(index);
        const blob = await canvasToBlob(canvas);
        zip.file(`KNFC_cardnews_${String(index + 1).padStart(2, '0')}.jpg`, blob);
      }
      zip.file('SNS_게시글.txt', `${caption}\n\n${hashtags.map((tag) => `#${tag.replace(/^#/, '')}`).join(' ')}`);
      const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
      downloadBlob(blob, 'KNFC_cardnews.zip');
    } catch (downloadError) {
      setError(downloadError.message);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="cardnews-page">
      <div className="page-header">
        <div>
          <h1 className="page-title"><Images size={21} color="#fbbf24" /> SNS 카드뉴스 제작</h1>
          <p className="cardnews-subtitle">HWPX 보도자료와 사진으로 1080×1350 카드뉴스를 만듭니다.</p>
        </div>
        {result && <span className={`cardnews-mode ${result.generationMode}`}>{result.generationMode === 'ai' ? 'AI 초안' : '규칙 기반 초안'}</span>}
      </div>
      <input ref={photoInput} hidden type="file" accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" multiple onChange={handlePhotoSelection} />

      {!result && (
        <form className="card cardnews-upload" onSubmit={analyze}>
          <div className="cardnews-upload-heading"><Sparkles size={21} /><div><strong>보도자료 불러오기</strong><span>원문은 서버 메모리에서 분석되며 별도로 저장하지 않습니다.</span></div></div>
          <div className="cardnews-drop-grid">
            <button type="button" className={`cardnews-drop ${documentFile ? 'selected' : ''}`} onClick={() => documentInput.current?.click()}>
              <FileText size={34} />
              <strong>{documentFile ? documentFile.name : 'HWPX 파일 선택'}</strong>
              <span>최대 20MB</span>
            </button>
            <button type="button" className={`cardnews-drop ${photoFiles.length ? 'selected' : ''}`} onClick={() => photoInput.current?.click()}>
              <FileImage size={34} />
              <strong>{photoFiles.length ? `추가 사진 ${photoFiles.length}장` : 'JPG·PNG 추가'}</strong>
              <span>문서 내장 사진은 자동 추출</span>
            </button>
          </div>
          <input ref={documentInput} hidden type="file" accept=".hwpx,application/zip" onChange={(event) => setDocumentFile(event.target.files?.[0] || null)} />
          {error && <div className="cardnews-error">{error}</div>}
          <button className="btn btn-primary cardnews-analyze" disabled={loading || !documentFile} type="submit">
            {loading ? <><Loader2 size={17} className="spin" /> 보도자료 분석 중...</> : <><UploadCloud size={17} /> 카드뉴스 초안 만들기</>}
          </button>
        </form>
      )}

      {result && selectedCard && (
        <>
          <div className="cardnews-toolbar card">
            <div className="cardnews-style-controls">
              <div className="cardnews-theme-list">
                <span>디자인</span>
                {[['classic', 'KNFC 블루'], ['energy', '에너지 그린'], ['clean', '모던 화이트']].map(([value, label]) => <button key={value} className={theme === value ? 'active' : ''} onClick={() => setTheme(value)}><i style={{ background: THEMES[value].primary }} />{label}</button>)}
              </div>
              <label className="cardnews-font-select">폰트<select className="select" value={font} onChange={(event) => setFont(event.target.value)}>{Object.entries(FONTS).map(([value, item]) => <option key={value} value={value}>{item.label}</option>)}</select></label>
            </div>
            <div className="cardnews-template-controls"><input className="input" value={templateName} maxLength={40} placeholder="현재 설정을 템플릿으로 저장" onChange={(event) => setTemplateName(event.target.value)} /><button className="btn btn-secondary btn-sm" onClick={saveTemplate}>템플릿 저장</button><select className="select" value="" onChange={(event) => { const template = templates.find((item) => item.id === event.target.value); if (template) applyTemplate(template); }}><option value="">템플릿 불러오기</option>{templates.map((template) => <option key={template.id} value={template.id}>{template.name} · {TYPE_LABELS[template.type]}</option>)}</select>{templates.length > 0 && <div className="cardnews-template-list">{templates.map((template) => <span key={template.id}><button onClick={() => applyTemplate(template)}>{template.name}</button><button onClick={() => duplicateTemplate(template)}>복제</button><button className="danger" onClick={() => deleteTemplate(template.id)}>삭제</button></span>)}</div>}</div>
            <div className="cardnews-project-controls">
              <input className="input" value={projectName} maxLength={100} placeholder={currentProjectId ? '현재 프로젝트 이름' : '새 프로젝트 이름'} onChange={(event) => setProjectName(event.target.value)} />
              <button className="btn btn-secondary btn-sm" disabled={savingProject} onClick={saveProject}>{savingProject ? <Loader2 size={14} className="spin" /> : <Save size={14} />} {currentProjectId ? '업데이트' : '저장'}</button>
              <select className="select" value="" onChange={(event) => loadProject(event.target.value)}><option value="">{projects.length ? '프로젝트 불러오기' : '저장된 프로젝트 없음'}</option>{projects.map((project) => <option key={project.id} value={project.id}>{project.title} · {new Date(project.updated_at).toLocaleDateString('ko-KR')}</option>)}</select>
              {projects.length > 0 && <div className="cardnews-project-list">{projects.map((project) => <span key={project.id} className={currentProjectId === project.id ? 'active' : ''}><button onClick={() => loadProject(project.id)}>{project.title}</button><button className="danger" onClick={() => removeProject(project.id)}><Trash2 size={12} /></button></span>)}</div>}
            </div>
            <div className="cardnews-toolbar-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => photoInput.current?.click()}><FileImage size={14} /> 사진 추가</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setResult(null)}>새로 만들기</button>
              <button className="btn btn-secondary btn-sm" disabled={downloading} onClick={downloadCurrent}><Download size={14} /> 현재 장</button>
              <button className="btn btn-primary btn-sm" disabled={downloading} onClick={downloadZip}>{downloading ? <Loader2 size={14} className="spin" /> : <Download size={14} />} ZIP 다운로드</button>
            </div>
          </div>

          {error && <div className="cardnews-error">{error}</div>}
          <div className="cardnews-workspace">
            <aside className="card cardnews-list">
              <div className="cardnews-panel-title"><span>카드 구성</span><button onClick={addCard} disabled={cards.length >= 8}><Plus size={14} /></button></div>
              {cards.map((card, index) => (
                <button key={card.id} className={`cardnews-list-item ${activeCard === index ? 'active' : ''}`} onClick={() => setActiveCard(index)}>
                  <span>{String(index + 1).padStart(2, '0')}</span><div><strong>{card.title || '제목 없음'}</strong><small>{TYPE_LABELS[card.type]}</small></div>
                </button>
              ))}
            </aside>

            <section className="cardnews-preview-wrap">
              <PreviewCard card={selectedCard} index={activeCard} total={cards.length} image={selectedImage} themeName={theme} fontName={font} layoutEditing={layoutEditing} onStartMove={startLayoutMove} onStartResize={startLayoutResize} />
              <div className="cardnews-preview-note">실제 다운로드는 1080×1350 JPG로 생성됩니다.</div>
            </section>

            <aside className="card cardnews-editor">
              <div className="cardnews-panel-title"><span>{activeCard + 1}번 카드 편집</span><button className="danger" onClick={removeCard}><Trash2 size={14} /></button></div>
              <label>유형<select className="select" value={selectedCard.type} onChange={(event) => updateCard('type', event.target.value)}>{Object.entries(TYPE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
              <label>상단 분류<input className="input" maxLength={30} value={selectedCard.eyebrow} onChange={(event) => updateCard('eyebrow', event.target.value)} /></label>
              <label>제목 <span>{selectedCard.title.length}/70</span><textarea className="input" rows="2" maxLength={70} value={selectedCard.title} onChange={(event) => updateCard('title', event.target.value)} /></label>
              <label>본문 <span>{selectedCard.body.length}/220</span><textarea className="input" rows="6" maxLength={220} value={selectedCard.body} onChange={(event) => updateCard('body', event.target.value)} /></label>
              {selectedCard.type === 'number' && <label>강조 수치<input className="input" maxLength={30} value={selectedCard.highlight} onChange={(event) => updateCard('highlight', event.target.value)} /></label>}
              {selectedTextStyles && <>
                <TypographyControls label="제목" prefix="title" style={selectedTextStyles.title} minSize={30} maxSize={110} updateCard={updateCard} />
                <TypographyControls label="본문" prefix="body" style={selectedTextStyles.body} minSize={18} maxSize={64} updateCard={updateCard} />
                {selectedCard.type === 'number' && <TypographyControls label="강조 수치" prefix="highlight" style={selectedTextStyles.highlight} minSize={64} maxSize={210} updateCard={updateCard} />}
              </>}
              <label>사진<select className="select" value={selectedCard.imageId} onChange={(event) => updateCard('imageId', event.target.value)}><option value="">사진 없음</option>{images.map((image) => <option key={image.id} value={image.id}>{image.name}</option>)}</select></label>
              <div className="cardnews-layout-controls">
                <div className="cardnews-layout-heading"><strong>레이아웃 조정</strong><span><button type="button" className={layoutEditing ? 'active' : ''} onClick={() => setLayoutEditing((current) => !current)}>{layoutEditing ? '이동 완료' : '직접 이동'}</button><button type="button" onClick={resetLayout}>초기화</button></span></div>
                <p>{layoutEditing ? '파란 영역을 끌어 이동하고, 오른쪽 아래 핸들을 끌어 크기를 조정하세요.' : '직접 이동을 누르면 미리보기에서 터치로 위치와 크기를 조정할 수 있습니다.'}</p>
                <div className="cardnews-layout-subheading">텍스트 영역</div>
                <label>가로 위치 <span>{selectedTextLayout.x}px</span><input type="range" min="0" max={1080 - selectedTextLayout.width} step="1" value={selectedTextLayout.x} onChange={(event) => updateLayout('text', 'x', event.target.value)} /></label>
                <label>세로 위치 <span>{selectedTextLayout.y}px</span><input type="range" min="180" max="1200" step="1" value={selectedTextLayout.y} onChange={(event) => updateLayout('text', 'y', event.target.value)} /></label>
                <label>너비 <span>{selectedTextLayout.width}px</span><input type="range" min="240" max="1080" step="1" value={selectedTextLayout.width} onChange={(event) => updateLayout('text', 'width', event.target.value)} /></label>
                {selectedImage && selectedCard.type !== 'number' && selectedCard.type !== 'cover' && <>
                  <div className="cardnews-layout-subheading">사진 영역</div>
                  <label>가로 위치 <span>{selectedPhotoLayout.x}px</span><input type="range" min="0" max={1080 - selectedPhotoLayout.width} step="1" value={selectedPhotoLayout.x} onChange={(event) => updateLayout('photo', 'x', event.target.value)} /></label>
                  <label>세로 위치 <span>{selectedPhotoLayout.y}px</span><input type="range" min="150" max={1350 - selectedPhotoLayout.height} step="1" value={selectedPhotoLayout.y} onChange={(event) => updateLayout('photo', 'y', event.target.value)} /></label>
                  <label>너비 <span>{selectedPhotoLayout.width}px</span><input type="range" min="240" max="1080" step="1" value={selectedPhotoLayout.width} onChange={(event) => updateLayout('photo', 'width', event.target.value)} /></label>
                  <label>높이 <span>{selectedPhotoLayout.height}px</span><input type="range" min="140" max="900" step="1" value={selectedPhotoLayout.height} onChange={(event) => updateLayout('photo', 'height', event.target.value)} /></label>
                </>}
              </div>
              {selectedImage && selectedCard.type !== 'number' && <div className="cardnews-crop-controls">
                <div className="cardnews-crop-heading"><strong>사진 위치·크롭</strong><button type="button" onClick={() => setCards((current) => current.map((card, index) => index === activeCard ? { ...card, imageZoom: 1, imagePositionX: 50, imagePositionY: 50 } : card))}>초기화</button></div>
                <label>확대 <span>{Number(selectedCard.imageZoom ?? 1).toFixed(1)}×</span><input type="range" min="1" max="3" step="0.1" value={selectedCard.imageZoom ?? 1} onChange={(event) => updateCard('imageZoom', Number(event.target.value))} /></label>
                <label>가로 초점 <span>{selectedCard.imagePositionX ?? 50}%</span><input type="range" min="0" max="100" step="1" value={selectedCard.imagePositionX ?? 50} onChange={(event) => updateCard('imagePositionX', Number(event.target.value))} /></label>
                <label>세로 초점 <span>{selectedCard.imagePositionY ?? 50}%</span><input type="range" min="0" max="100" step="1" value={selectedCard.imagePositionY ?? 50} onChange={(event) => updateCard('imagePositionY', Number(event.target.value))} /></label>
                <p>슬라이더로 사진의 확대와 보이는 중심을 조정합니다.</p>
              </div>}
            </aside>
          </div>

          <div className="card cardnews-caption-editor">
            <div><label>게시글 문안 <span>{caption.length}/1000</span></label><textarea className="input" rows="7" maxLength={1000} value={caption} onChange={(event) => setCaption(event.target.value)} /></div>
            <div><label>해시태그</label><input className="input" value={hashtags.map((tag) => `#${tag.replace(/^#/, '')}`).join(' ')} onChange={(event) => setHashtags(event.target.value.split(/\s+/).map((tag) => tag.replace(/^#/, '')).filter(Boolean).slice(0, 8))} /><div className="cardnews-source"><strong>추출 원문</strong><span>{result.document.paragraphs.length}개 문단 · 사진 {images.length}장</span><details><summary>원문 확인</summary><pre>{result.document.text}</pre></details></div></div>
          </div>
        </>
      )}
    </div>
  );
}
